import { registerAccount, applySession, type RegisterInput } from './auth';
import type { RegistrationContext } from '../workflow/machines/registrationMachine';
import { deriveGeocode, nameToCoords } from '../geo/places';

/**
 * Registration payload builder — maps wizard machine context to `RegisterDto`
 * per the payload matrix: `candidate`/`both` send `profile`, `employer`/`both`
 * send `employerProfile`, and a missing role is a hard error — never a silent
 * candidate fallback.
 *
 * @see Documentation/Specification/04_Processes/application-specific/workseeking/CandidateAndEmployerRegistration.md §5
 * @see Documentation/Specification/020_Definitions/application-agnostic/auth/Register.md
 */
export interface RegistrationResult {
  candidateProfileId?: string;
  employerProfileId?: string;
  localOnly: boolean;
}

export async function submitRegistration(
  ctx: RegistrationContext,
): Promise<RegistrationResult> {
  const result: RegistrationResult = { localOnly: false };

  // 1. Create the account (required for authentication + email verification).
  if (!ctx.account) {
    throw new Error('Missing account details. Please go back and re-enter them.');
  }

  // Branch authority: no role, no registration (spec §2 rule 4).
  if (!ctx.role) {
    throw new Error('Pick how you will use BluePages before submitting.');
  }

  // Build candidate profile data if role includes candidate
  let profile: RegisterInput['profile'] | undefined;
  if (ctx.role === 'candidate' || ctx.role === 'both') {
    // Resolve the free-text city to a privacy-safe geocode (Plus Code + H3),
    // never persisting raw coordinates.
    const desiredLocations: NonNullable<RegisterInput['profile']>['desiredLocations'] = [];
    if (ctx.city) {
      const place = await nameToCoords(ctx.city);
      if (place) {
        const geo = deriveGeocode({ latitude: place.latitude, longitude: place.longitude }, 'Public');
        desiredLocations.push({
          label: place.name,
          plusCode: geo.plusCode,
          plusCodeShort: geo.plusCodeShort ?? '',
          h3Index: geo.h3Index,
          h3Resolution: geo.h3Resolution,
          geocodePrecision: geo.geocodePrecision,
          radiusKm: 50,
        });
      } else {
        desiredLocations.push({ label: ctx.city, plusCodeShort: '', h3Index: '', h3Resolution: 0, geocodePrecision: 'Public', radiusKm: 50 });
      }
    }

    profile = {
      profileType: ctx.profileType ?? 'Individual',
      headline: ctx.headline,
      availability: ctx.availability as NonNullable<RegisterInput['profile']>['availability'],
      workType: ctx.workType as NonNullable<RegisterInput['profile']>['workType'],
      skills: ctx.skills,
      desiredLocations,
    };
  }

  // Company payload for employer-capable roles — guest input only (spec §4).
  let employerProfile: RegisterInput['employerProfile'] | undefined;
  if (ctx.role === 'employer' || ctx.role === 'both') {
    const companyName = ctx.companyName.trim();
    if (companyName.length < 2) {
      throw new Error('Company name must be at least 2 characters.');
    }
    employerProfile = {
      companyName,
      industry: ctx.industry.trim() || undefined,
      companySize: ctx.companySize || undefined,
    };
  }

  const name = `${ctx.account.firstName} ${ctx.account.lastName}`.trim() || ctx.account.email;
  const reg = await registerAccount({
    email: ctx.account.email,
    password: ctx.account.password,
    firstName: ctx.account.firstName,
    lastName: ctx.account.lastName,
    role: ctx.role,
    nationalId: ctx.nationalId ? { type: ctx.nationalId.type as 'NationalId' | 'Passport' | 'TaxId', number: ctx.nationalId.number, country: ctx.nationalId.country } : undefined,
    profile,
    employerProfile,
  });
  applySession(reg, { email: ctx.account.email, name });

  // Store candidate profile ID if created
  if (reg.candidateProfileId) {
    result.candidateProfileId = reg.candidateProfileId;
    localStorage.setItem('bp_candidate_profile_id', reg.candidateProfileId);
  }

  // Spec §6: the employer profile id from the response is stored, never dropped.
  if (reg.employerProfileId) {
    result.employerProfileId = reg.employerProfileId;
    localStorage.setItem('bp_employer_profile_id', reg.employerProfileId);
  }

  return result;
}
